import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, articles } from '@/mocks/articles';

const faqs = [
  {
    q: '「太子千秋万载」最早出现在什么时代？',
    a: '「千秋万载」作为完整的祝颂之辞最早可追溯至隋唐时期。先秦时代已有「千秋」「万寿」等词汇出现在祭祀与祝颂活动中，但其作为对太子的标准祝词则是在唐代礼制完备后才正式定型。',
  },
  {
    q: '「千秋万载」与皇帝用的「万岁」有什么区别？',
    a: '两者有着严格的等级区分：「万岁万岁万万岁」是皇帝专属祝词，寓意天子至高无上、福寿无疆；「千秋万载」则是太子的专属祝颂，语义虽同样表达长久祝愿，但「千」低于「万」，体现了储君虽尊但不可僭越的礼制等级。',
  },
  {
    q: '除了「太子千秋万载」，还有哪些皇室祝颂用语？',
    a: '皇室祝颂有一套完整的等级体系：皇帝用「万岁」、皇后与太后用「千岁」、太子用「千秋万载」、皇子用「千岁」、皇孙用「百岁」。不同朝代在具体措辞上略有差异，但基本的等级逻辑贯穿始终。',
  },
  {
    q: '「千秋节」和「太子千秋万载」有关系吗？',
    a: '密切相关。千秋节由唐玄宗于开元十七年设立，是最初为皇帝寿诞而设的节日。虽然千秋节并非专门针对太子，但其命名中「千秋」一词本身就与太子祝颂有着深厚的语义关联，节日庆典中也包含对太子的祝颂环节。',
  },
  {
    q: '影视剧中的「太子千秋万载」符合历史事实吗？',
    a: '大多数清宫剧和古装剧在呈现太子祝颂场景时，在礼仪规制上基本遵循了历史记载，但在具体的祝颂方式和频率上进行了戏剧化处理以增强观赏性。值得注意的是，历史上太子祝颂仅用于特定的正式礼仪场合，并非日常用语。',
  },
  {
    q: '本站的内容来源是否可靠？',
    a: '本站所有文章均参考《唐六典》《大唐开元礼》《明集礼》《大清通礼》等官方典籍，以及《史记》《汉书》《资治通鉴》等正史资料，由专业的历史文化研究者撰写，力求在学术严谨性与可读性之间取得最佳平衡。',
  },
];

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const recentArticles = articles.filter((a) => !a.featured).slice(0, 6);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '太子千秋万载——皇储祝颂文化',
    description: '深度解析「太子千秋万载」的历史渊源、礼仪场景、文化内涵与影视戏曲中的经典呈现，带您领略皇储文化的深厚底蕴。',
    url: 'https://yanxiya.cn/',
    isPartOf: {
      '@type': 'WebSite',
      name: '太子千秋万载文化研究',
      url: 'https://yanxiya.cn/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://yanxiya.cn/',
        },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="太子千秋万载——皇储祝颂文化 | 深度解析古代储君礼制与皇室祝词"
        description="「太子千秋万载」是古代皇室对太子的至高祝颂，承载千年礼制文化与储君荣耀。深度解析历史渊源、礼仪场景、文化内涵与影视呈现。"
        keywords="太子千秋万载,太子千秋万载典故,皇太子祝颂礼仪,古代储君文化,千秋万载历史渊源,皇室礼制祝词,影视宫廷经典台词"
        canonicalUrl="https://yanxiya.cn/"
        ogType="website"
        schemaJson={homeSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20imperial%20palace%20at%20sunset%2C%20golden%20dragon%20motifs%20on%20red%20walls%2C%20traditional%20Chinese%20architecture%2C%20Forbidden%20City%20style%2C%20warm%20golden%20light%2C%20soft%20clouds%20in%20sky%2C%20artistic%20painting%20style%2C%20solemn%20and%20magnificent%20atmosphere%2C%20rich%20red%20and%20gold%20tones&width=1800&height=900&seq=hero-bg-main&orientation=landscape"
              alt="太子千秋万载——皇储祝颂文化"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-300/30 mb-6">
                <i className="ri-vip-crown-line text-accent-400 text-sm"></i>
                <span className="text-sm text-accent-200 font-medium">千年礼制 · 储君荣耀</span>
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-background-50 mb-5 leading-tight">
                太子千秋万载
              </h1>
              <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
                古代皇室对太子的至高祝颂，承载千年礼制文化与储君荣耀。
                深度解析历史渊源、礼仪场景、文化内涵与影视戏曲中的经典呈现。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/lishi-yuanyuan"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  探索历史渊源
                </Link>
                <Link
                  to="/category/wenhua-neihan"
                  className="w-full sm:w-auto px-8 py-3 bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-lg font-medium hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-lightbulb-line mr-2"></i>
                  解读文化内涵
                </Link>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-8 py-16 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  四大文化板块
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                  从不同维度全方位解读皇储祝颂文化，为您呈现完整的知识体系
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {categories.map((cat) => {
                  return (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="relative w-full h-44 overflow-hidden bg-background-200">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-3 left-4 right-4">
                          <div className="flex items-center gap-2 text-background-50">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/80">
                              <i className={`${cat.icon} text-sm`}></i>
                            </div>
                            <span className="text-sm font-medium">{cat.articleCount} 篇文章</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                          {cat.name}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                          {cat.description}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    精选文章
                  </h2>
                  <p className="text-sm text-foreground-500">
                    编辑精选的高质量深度好文
                  </p>
                </div>
                <Link
                  to="/category/lishi-yuanyuan"
                  className="hidden sm:flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-8 py-16 md:py-20">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-500">
                    持续更新，为您带来更多精彩内容
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">
                  关于太子千秋万载，您可能想了解的问题
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-background-100 transition-colors list-none">
                      <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                        {faq.q}
                      </span>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-6 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>

              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faqs.map((faq) => ({
                      '@type': 'Question',
                      name: faq.q,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq.a,
                      },
                    })),
                  }),
                }}
              />
            </div>
          </section>

          <section className="w-full px-4 md:px-8 py-16 md:py-20 bg-primary-900">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                探寻千年礼制，传承文化薪火
              </h2>
              <p className="text-sm md:text-base text-background-300 mb-8 leading-relaxed">
                太子千秋万载不仅是历史的回响，更是中华礼制文明的璀璨瑰宝。
                加入我们，一同探索这份跨越千年的文化传承。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/lishi-yuanyuan"
                  className="w-full sm:w-auto px-8 py-3 bg-accent-500 text-primary-950 rounded-lg font-semibold hover:bg-accent-400 transition-all duration-300 whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="w-full sm:w-auto px-8 py-3 bg-background-50/10 text-background-50 border border-background-50/20 rounded-lg font-medium hover:bg-background-50/15 transition-all duration-300 whitespace-nowrap"
                >
                  关于我们
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}